import { useSuspenseQuery } from '@tanstack/react-query';
import { request } from '../axios';
import type { GetPatientsBasicsParams, PatientBasicsDto } from '../generated/model';
import { getPaginatedContent, type PaginatedResponse } from '../generated/pagination/pagination.types';

const PATIENTS_PAGE_SIZE = 200;

type PatientsBasicsResponse = PatientBasicsDto[] | PaginatedResponse<PatientBasicsDto>;

const getPatientsBasicsPage = (params: GetPatientsBasicsParams, page: number, signal?: AbortSignal) => {
  return request<PatientsBasicsResponse>({
    url: `/api/patients`,
    method: 'GET',
    params: { ...params, page, size: PATIENTS_PAGE_SIZE },
    signal,
  });
};

const getAllPatientsBasics = async (params: GetPatientsBasicsParams, signal?: AbortSignal) => {
  const firstPage = await getPatientsBasicsPage(params, 0, signal);
  const patients = getPaginatedContent(firstPage);

  if (Array.isArray(firstPage)) {
    return patients;
  }

  const size = firstPage.size || PATIENTS_PAGE_SIZE;
  const pageCount = Math.ceil((firstPage.totalCount || 0) / size);
  if (pageCount <= 1) {
    return patients;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) => getPatientsBasicsPage(params, index + 1, signal)),
  );

  return [...patients, ...remainingPages.flatMap((page) => getPaginatedContent(page))];
};

export const getGetAllPatientsBasicsQueryKey = (params: GetPatientsBasicsParams) => {
  return [`/api/patients`, params, 'all'] as const;
};

/**
 * Loads every page of `/api/patients` so lookups by patient id cover all patients,
 * not only the first page returned by the paginated endpoint.
 */
export function useGetAllPatientsBasicsSuspense(params: GetPatientsBasicsParams) {
  return useSuspenseQuery({
    queryKey: getGetAllPatientsBasicsQueryKey(params),
    queryFn: ({ signal }) => getAllPatientsBasics(params, signal),
  });
}
